/**
 * CustomIndicatorPanel — editor for user-defined formula indicators.
 *
 * Allows users to write custom indicators using the
 * `custom-indicator-engine` DSL (`PLOT`, `COLOR`, `PANE` directives).
 *
 * Features:
 * - Syntax-highlighted code editor overlay
 * - Built-in templates (EMA Spread, Price Momentum, True Range, etc.)
 * - Saved indicators loaded from / persisted to localStorage
 * - Apply / Delete / New workflow
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  loadCustomIndicators,
  saveCustomIndicator,
  deleteCustomIndicator,
  parseCustomIndicator,
  customIndicatorToDefinition,
} from "../../service/custom-indicator-engine";
import "./CustomIndicatorPanel.scss";

interface CustomIndicatorPanelProps {
  onClose: () => void;
  onApply: (indicatorDef: any) => void;
  colorTheme: string;
}

const TEMPLATES: Record<string, string> = {
  "EMA Spread": `// EMA Spread Indicator
// Shows the difference between fast and slow EMA
PANE separate
PLOT spread = EMA(CLOSE, 9) - EMA(CLOSE, 21)
PLOT zero = 0
COLOR spread = #2196f3
COLOR zero = #666666`,

  "Price Momentum": `// Price Momentum
// Close vs N bars ago
PANE separate
PLOT momentum = CLOSE - PREV(CLOSE, 14)
COLOR momentum = #ff9800`,

  "True Range": `// True Range
PANE separate
PLOT tr = ATR(1)
PLOT avg_tr = ATR(14)
COLOR tr = #9c27b0
COLOR avg_tr = #4caf50`,

  "Price Channel": `// Price Channel (overlay)
PLOT upper = PREV(HIGH, 1)
PLOT lower = PREV(LOW, 1)
PLOT mid = (PREV(HIGH, 1) + PREV(LOW, 1)) / 2
COLOR upper = #4caf50
COLOR lower = #f44336
COLOR mid = #ff9800`,

  "RSI Overlay": `// RSI in separate pane
PANE separate
PLOT rsi = RSI(14)
PLOT overbought = 70
PLOT oversold = 30
COLOR rsi = #2196f3
COLOR overbought = #f44336
COLOR oversold = #4caf50`,
};

const KEYWORD_COLORS: Record<string, string> = {
  PLOT: "#c792ea",
  COLOR: "#c792ea",
  PANE: "#c792ea",
  separate: "#f78c6c",
  overlay: "#f78c6c",
  CLOSE: "#82aaff",
  OPEN: "#82aaff",
  HIGH: "#82aaff",
  LOW: "#82aaff",
  VOLUME: "#82aaff",
  SMA: "#ffcb6b",
  EMA: "#ffcb6b",
  RSI: "#ffcb6b",
  ATR: "#ffcb6b",
  PREV: "#ffcb6b",
  ABS: "#ffcb6b",
  MAX: "#ffcb6b",
  MIN: "#ffcb6b",
};

function highlightCode(code: string): string {
  return code
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/(\/\/.*)/g, '<span style="color:#546e7a;font-style:italic">$1</span>')
    .replace(
      /\b(PLOT|COLOR|PANE|separate|overlay|CLOSE|OPEN|HIGH|LOW|VOLUME|SMA|EMA|RSI|ATR|PREV|ABS|MAX|MIN)\b/g,
      (match) => {
        const color = KEYWORD_COLORS[match] || "#c3cee3";
        return `<span style="color:${color}">${match}</span>`;
      }
    )
    .replace(/(#[0-9a-fA-F]{6})/g, '<span style="color:$1">$1</span>')
    .replace(/\b(\d+\.?\d*)\b/g, '<span style="color:#f78c6c">$1</span>');
}

const CustomIndicatorPanel: React.FC<CustomIndicatorPanelProps> = ({
  onClose,
  onApply,
  colorTheme,
}) => {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [savedIndicators, setSavedIndicators] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const preRef = useRef<HTMLPreElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSavedIndicators(loadCustomIndicators());
  }, []);

  // Click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    setTimeout(() => document.addEventListener("mousedown", handler), 0);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const syncScroll = useCallback(() => {
    if (textareaRef.current && preRef.current) {
      preRef.current.scrollTop = textareaRef.current.scrollTop;
      preRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  }, []);

  const handleSave = () => {
    if (!name.trim()) { setError("Enter a name for the indicator"); return; }
    if (!code.trim()) { setError("Enter indicator code"); return; }
    saveCustomIndicator(name.trim(), code);
    setSavedIndicators(loadCustomIndicators());
    setError(null);
    setSuccess(`Saved "${name.trim()}"`);
    setTimeout(() => setSuccess(null), 2000);
  };

  const handleLoad = (indicatorName: string) => {
    const all = loadCustomIndicators();
    if (all[indicatorName]) {
      setName(indicatorName);
      setCode(all[indicatorName]);
      setError(null);
    }
  };

  const handleDelete = (indicatorName: string) => {
    deleteCustomIndicator(indicatorName);
    setSavedIndicators(loadCustomIndicators());
    if (name === indicatorName) {
      setName("");
      setCode("");
    }
  };

  const handleLoadTemplate = (templateName: string) => {
    setCode(TEMPLATES[templateName]);
    setName(templateName);
    setError(null);
  };

  const handleApply = () => {
    if (!name.trim()) { setError("Enter a name for the indicator"); return; }
    if (!code.trim()) { setError("Enter indicator code"); return; }
    try {
      const parsed = parseCustomIndicator(name.trim(), code);
      if (parsed.plots.length === 0) {
        setError("No PLOT statements found. Use: PLOT name = expression");
        return;
      }
      const def = customIndicatorToDefinition(parsed);
      onApply(def);
      setError(null);
      setSuccess(`Applied "${name.trim()}" to chart`);
      setTimeout(() => setSuccess(null), 2000);
    } catch (e: any) {
      setError(e.message || "Parse error");
    }
  };

  const lineNumbers = code.split("\n").map((_, i) => i + 1);

  return (
    <div className={`custom-indicator-panel ${colorTheme}`} ref={panelRef}>
      <div className="cip-header">
        <h3>Custom Indicator Editor</h3>
        <button className="cip-close" onClick={onClose}>✕</button>
      </div>

      <div className="cip-body">
        <div className="cip-sidebar">
          <div className="cip-section">
            <h4>Templates</h4>
            {Object.keys(TEMPLATES).map((t) => (
              <button key={t} className="cip-item" onClick={() => handleLoadTemplate(t)}>
                {t}
              </button>
            ))}
          </div>

          <div className="cip-section">
            <h4>Saved</h4>
            {Object.keys(savedIndicators).length === 0 && (
              <span className="cip-empty">No saved indicators</span>
            )}
            {Object.keys(savedIndicators).map((n) => (
              <div key={n} className="cip-saved-item">
                <button className="cip-item" onClick={() => handleLoad(n)}>
                  {n}
                </button>
                <button className="cip-delete" onClick={() => handleDelete(n)} title="Delete">
                  ✕
                </button>
              </div>
            ))}
          </div>

          <div className="cip-section cip-help">
            <h4>Syntax</h4>
            <code>PLOT name = expr</code>
            <code>COLOR name = #hex</code>
            <code>PANE separate</code>
            <p>Fields: CLOSE, OPEN, HIGH, LOW, VOLUME</p>
            <p>Functions: SMA(n), EMA(n), RSI(n), ATR(n), PREV(expr, n), ABS(), MAX(), MIN()</p>
            <p>Operators: + - * /</p>
          </div>
        </div>

        <div className="cip-editor-area">
          <div className="cip-name-row">
            <input
              type="text"
              placeholder="Indicator name..."
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="cip-name-input"
            />
            <button className="cip-btn cip-btn-save" onClick={handleSave}>Save</button>
            <button className="cip-btn cip-btn-apply" onClick={handleApply}>Apply to Chart</button>
          </div>

          <div className="cip-code-editor">
            <div className="cip-line-numbers">
              {lineNumbers.map((n) => (
                <div key={n} className="cip-line-num">{n}</div>
              ))}
            </div>
            <div className="cip-code-wrapper">
              <pre
                ref={preRef}
                className="cip-highlight"
                dangerouslySetInnerHTML={{ __html: highlightCode(code) + "\n" }}
              />
              <textarea
                ref={textareaRef}
                value={code}
                onChange={(e) => { setCode(e.target.value); setError(null); }}
                onScroll={syncScroll}
                spellCheck={false}
                className="cip-textarea"
                placeholder="// Write your custom indicator code here..."
              />
            </div>
          </div>

          {error && <div className="cip-error">{error}</div>}
          {success && <div className="cip-success">{success}</div>}
        </div>
      </div>
    </div>
  );
};

export default CustomIndicatorPanel;
